import { ThemeAPI } from './api'
import { ApisauceInstance } from 'apisauce'
import { ThemePackageOptions } from './types'
import { generateColors } from './utils'
import { themeStore } from '@codeleap/styles'
import { waitFor } from '@codeleap/utils'

export class ThemePackage {
  public API: ThemeAPI
  protected api: ApisauceInstance
  protected options: ThemePackageOptions
  protected isDev = false


  get colorStore(){
    return this.options.colorStore
  }

  getTheme(){
    return this.options.getTheme()
  }

  get colorProxy(){
    const store = this.colorStore.get()

    return new Proxy(this.options.baseColors, {
      get(target, p:string, receiver) {
        if(store[p]){
          return store[p]
        }

        return Reflect.get(target, p, receiver)
      },
    })
  }

  constructor(api: ApisauceInstance, options: ThemePackageOptions, isDev: boolean = false) {
    this.api = api
    this.API = new ThemeAPI(this.api, isDev)
    this.options = options
    this.isDev = isDev
  }

  async prepareTheme(){
    const companyTheme = await this.API.retrieve()
 
    if(!companyTheme?.primaryColour) return [null, null]

    return generateColors({
      primary: companyTheme.primaryColour,
      baseColors: this.options.baseColors,
      draft: this.options.draftMode
    })
  }

  async isColorSchemeApplied(name:string){
    const theme = await this.getTheme()
    const currentScheme = theme.currentColorScheme()
    
    return currentScheme === name
  }

  isColorSchemeRegistered(name:string){
    return !!themeStore.alternateColorsScheme?.[name]
  }

  async unload(name:string){
    const theme = await this.getTheme()
    this.colorStore.remove()
    theme.setColorScheme('default')
    theme.ejectColorScheme(name)
  }
  loadTheme(name:string, forceRefresh = false){
    return new Promise<{reload: boolean}>(async (resolve, reject) => {
      const theme = await this.getTheme()
      const isApplied = await this.isColorSchemeApplied(name)
      const isRegistered = this.isColorSchemeRegistered(name)

      let resolved = false

      const onResolve = (reload: boolean) => {
        if(!resolved){ // Ensures the promise only resolves once
          resolved = true
          resolve({ reload })
        }
      }
 
      
      if(isRegistered){ // If the theme already exists, we resolve early and tell the app to apply it if needed
        theme.setColorScheme(name)
        if(!forceRefresh){
          onResolve(!isApplied)
        }
      }

      // Always revalidate the local theme, and resolve once it's been injected, telling the app to apply it if it's not already set
      this.prepareTheme().then(async ([colorMap, overrides]) => {
                
        if(!colorMap){
          onResolve(false)
          return
        }
        if(overrides){
          this.colorStore.set(overrides)
          await waitFor(500)
        }

        theme.injectColorScheme(name, colorMap)
        theme.setColorScheme(name)
        
        onResolve(!isApplied)
      }).catch((e) => {
        if(!resolved){
          reject()
        } else {
          console.error(e)
        }
      })
      
    })   
  }

}

export * from './types'
